/* eslint-disable react/prop-types */
import { MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState, useCallback } from 'react';
import { Controlled as ControlledZoom } from 'react-medium-image-zoom';
import { useForm } from 'react-hook-form';
import { connect, useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import pluralize from 'pluralize';
import {
  addToConfigStack,
  clearProductConfig,
  popItemFromConfig,
  setProductConfig,
  addToCart,
} from '../../../actions/v3Order';
import cartUtils from '../../../utils/cart';
import formatUSD from '../../../utils/formatUSD';
import productConfigUtils from '../../../utils/productConfig';
import { shouldBreakConfigLoop } from '../helpers/config';
import { getProductByLocation } from '../helpers/store';
import useScreenSize from '../hooks/screen';
import DoneScreen from './done';
import {
  BackgroundNamePriceWrapper,
  CategoryButton,
  CategorySelector,
  ConfigScreenWrapper,
  Instruction,
  ItemImage,
  ItemImageBackgroundSelection,
  ItemImageBackgroundWrapper,
  ItemImageContainer,
  ItemPanel,
  ItemsGrid,
  ItemWrapper,
  PoseImage,
  ZoomButtonWrapper,
} from './productConfigStyles';
import SlotSelector from './slotSelector';
import RequiredProductForm from '../products/required-product-form/RequiredProductForm.jsx';
import ProductOptionsSelector from '../products/regular/subcomponents/ProductOptionsSelector';
import { addToConfig, getTotalPages } from './utils';

const ActionButton = styled.button`
  border: none;
  align-self: stretch;
  background: #479DE3;
  height: 42px;
  padding: 10px;
  font-size 14px;
  color: white;
  border-radius: 3px;
  font-weight: 800;
  margin-top: 30px;

  :disabled {
    background: gray;
    pointer-events: none;
    cursor: initial;
    opacity: 0.3;
  }
`;

const DoneWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: min-content;
  min-width: 260px;
`;

const getLastPoseUrl = (productConfig) => {
  const lastKey = Object.keys(productConfig).sort((a, b) => b - a)[0];
  const lastPose = productConfig[lastKey]?.pose;
  const url = lastPose?.selected_crop
    ? lastPose.selected_crop?.url
    : lastPose?.url;
  return url || null;
};

const Item = ({ src, largeSrc, onClick = () => {}, name, price, location }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const { pathname } = location;
  const { productConfig } = useSelector((state) => state.v3Order);
  const event = useSelector((state) => state.shoot);
  const poseUrl = getLastPoseUrl(productConfig);
  const { studentPreviewImageUrl, studentId } = useSelector(
    (state) => state.additionalData,
  );
  const orders = useSelector((state) => state.v3Order.orders);

  let isBackgroundSelection = false;

  const handleZoomChange = useCallback((shouldZoom) => {
    setIsZoomed(shouldZoom);
  }, []);

  const handleZoomClick = (e) => {
    e.stopPropagation();
    setIsZoomed((zoomed) => !zoomed);
  };

  if (pathname && pathname.includes('background')) {
    isBackgroundSelection = true;
  }

  let chargeForYpxBackgroundUpgrades;

  if (typeof event.chargeForYpxBackgroundUpgrades === 'boolean') {
    chargeForYpxBackgroundUpgrades = event.chargeForYpxBackgroundUpgrades;
  } else {
    chargeForYpxBackgroundUpgrades = true;
  }

  const orderContainsSingleBackgroundUpgrade =
    cartUtils.orderContainsSingleBackgroundUpgrade(orders, studentId);

  const productConfigContainsSingleBackgroundUpgrade =
    productConfigUtils.productConfigContainsSingleBackgroundUpgrade(
      productConfig,
    );

  return (
    <ItemWrapper>
      {isBackgroundSelection && (
        <>
          <ItemImageBackgroundWrapper>
            <ControlledZoom isZoomed={isZoomed} onZoomChange={handleZoomChange}>
              <ItemImageBackgroundSelection
                src={src}
                alt={name}
                onClick={onClick}
              />
              {(poseUrl || studentPreviewImageUrl) && (
                <PoseImage
                  src={poseUrl}
                  alt="Pose Image"
                  onContextMenu={(event) => event.preventDefault()}
                  onClick={onClick}
                />
              )}
            </ControlledZoom>
            <ZoomButtonWrapper onClick={handleZoomClick}>
              <MagnifyingGlassPlusIcon />
            </ZoomButtonWrapper>
          </ItemImageBackgroundWrapper>
          <BackgroundNamePriceWrapper>
            <p>
              {name}
              {price &&
                !orderContainsSingleBackgroundUpgrade &&
                !productConfigContainsSingleBackgroundUpgrade &&
                chargeForYpxBackgroundUpgrades && (
                  <>
                    <br />
                    {formatUSD(price)}
                  </>
                )}
            </p>
          </BackgroundNamePriceWrapper>
        </>
      )}
      {!isBackgroundSelection && (
        <>
          <ItemImageContainer>
            <ControlledZoom
              isZoomed={isZoomed}
              onZoomChange={handleZoomChange}
              zoomImg={{ src: largeSrc }}
            >
              <ItemImage
                src={src}
                onClick={onClick}
                alt={name}
                onContextMenu={(event) => event.preventDefault()}
              />
            </ControlledZoom>
            <ZoomButtonWrapper onClick={handleZoomClick}>
              <MagnifyingGlassPlusIcon />
            </ZoomButtonWrapper>
          </ItemImageContainer>
          <p>{name.length > 24 ? name.substring(0, 24) + '...' : name}</p>
        </>
      )}
    </ItemWrapper>
  );
};

const _ItemButton = ({ pages, history, item, cropIndex = null, ...props }) => {
  const { productConfig } = useSelector((s) => s.v3Order);
  const { enableZoomOnProducts } = useSelector((s) => s.additionalData);
  const event = useSelector((s) => s.shoot);
  const dispatch = useDispatch();

  const handleClick = () => {
    console.log('Item being added to config:', item);
    console.log('Item product_option_sets:', item.product_option_sets);
    addToConfig({
      dispatch,
      history,
      productConfig,
      pages,
      itemToAdd: item,
      event,
      cropIndex,
    });
  };

  return <Item price={item.price || null} {...props} onClick={handleClick} />;
};

const ItemButton = withRouter(_ItemButton);

export const ConfigScreen = ({
  shoot,
  items,
  categories,
  itemsDesktop,
  itemsMobile,
  instruction,
  gridStyle = {},
  children,
  ...props
}) => {
  const dispatch = useDispatch();
  const [screenSize, loadingScreenSize] = useScreenSize();
  const hash = props.location.hash?.replace('#', '');
  const product = getProductByLocation(shoot?.shootProducts, location);

  const {
    additional_data = {},
    additional_data: { free_indices = [] } = {},
    required_children_count,
  } = product;

  const isYouPickX = Object.keys(additional_data).length == 0;
  const isBYO = Object.keys(additional_data).length > 0;

  const { name, price, maximum_children_count: slotCount } = product;
  const hasHash = !!hash;
  const pages = getTotalPages(shoot, props.additionalData);
  const { productConfig } = useSelector((s) => s.v3Order);
  const { control, handleSubmit, register } = useForm();
  const [requiredProductFields, setRequiredProductFields] = useState([]);
  const [selectedProductOptions, setSelectedProductOptions] = useState({});

  useEffect(() => {
    const currentUrlSplit = props.history.location.pathname.split('/');
    const currentSection = currentUrlSplit[currentUrlSplit.length - 2];
    const currentIndex = parseInt(
      currentUrlSplit[currentUrlSplit.length - 1]?.replace('#slots', ''),
    );
    const item = productConfig[currentIndex];
    let requiredFieldsFromAllProducts = [];
    Object.keys(productConfig).forEach((key, index) => {
      if (productConfig[key].product?.required_product_fields?.length > 0) {
        requiredFieldsFromAllProducts.push(
          productConfig[key].product?.required_product_fields,
        );
      }
    });

    setRequiredProductFields(requiredFieldsFromAllProducts.flat());
    if (item && item[currentSection])
      dispatch(popItemFromConfig(currentIndex, currentSection));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [props.location]);

  const shouldShowSlots = screenSize !== 'Mobile' || hasHash;
  const shouldShowItems = screenSize !== 'Mobile' || !hasHash;

  const products = Object.values(productConfig).reduce(
    (a, b) => (b.product ? [...a, b.product] : a),
    [],
  );

  const background_choice_mode = useSelector(
    (state) => state.shoot?.shoot?.background_choice_mode,
  );
  const isGreenScreenEvent =
    background_choice_mode === 'advanced_greenscreen_backgrounds_only';

  const backgrounds = Object.values(productConfig).reduce(
    (a, b) => (b.background ? [...a, b.background] : a),
    [],
  );
  const poses = Object.values(productConfig).reduce(
    (a, b) => (b.pose ? [...a, b.pose] : a),
    [],
  );

  const minimumChildCount =
    isYouPickX && Number.isInteger(required_children_count)
      ? required_children_count
      : isYouPickX
      ? slotCount
      : 1;

  const actionButtonEnabled =
    (poses.length === 0 || poses.length === products.length) &&
    (backgrounds.length === 0 || backgrounds.length === products.length) &&
    [...products, ...poses, ...backgrounds].length >=
      pages.length * minimumChildCount;

  const handleProductOptionChange = (optionSetKey, optionValue) => {
    setSelectedProductOptions((prev) => ({
      ...prev,
      [optionSetKey]: optionValue,
    }));
  };

  const submitOrder = () => {
    handleSubmit((formState) => {
      // TODO: handle poses

      if (isBYO) {
        if ([...products].length < minimumChildCount)
          return Swal.fire(
            `Please configure at least ${minimumChildCount} item(s).`,
          );

        if (
          [...products].length > [...backgrounds].length &&
          isGreenScreenEvent
        )
          return Swal.fire(`Please finish configuring all items.`);
      }

      if (isYouPickX && !actionButtonEnabled) {
        return Swal.fire(
          `Please configure ${minimumChildCount} ${pluralize(
            'item',
            minimumChildCount,
          )}.`,
        );
      }

      dispatch(
        addToCart({
          studentId: props.additionalData.studentId,
          fullproductData: product,
          requiredFields: formState,
          productOptions: selectedProductOptions,
        }),
      );
    })();
  };

  if (loadingScreenSize) return <div />;

  const ItemGrid = ({
    itemsDesktop,
    itemsMobile,
    items,
    pages,
    history,
    gridStyle = {},
  }) => (
    <ItemsGrid
      itemsDesktop={itemsDesktop}
      itemsMobile={itemsMobile}
      style={gridStyle}
    >
      {items.map((item) => {
        if (item.cropped_images?.length > 0) {
          return item.cropped_images.map((image, i) => (
            <ItemButton
              key={`cropped-img-${image.id}`}
              history={history}
              pages={pages}
              id={item.id}
              name={image.name}
              src={image.url}
              shoot={shoot}
              item={item}
              cropIndex={i}
              {...props}
            />
          ));
        }
        return (
          <ItemButton
            key={item.id}
            history={history}
            pages={pages}
            id={item.id}
            name={item.display_name || item.name}
            src={item.image_thumbnail || item.url}
            largeSrc={item.image_large || null}
            {...props}
            shoot={shoot}
            item={item}
          />
        );
      })}
    </ItemsGrid>
  );

  const CategoryGrid = ({
    itemsDesktop,
    itemsMobile,
    pages,
    categories = [],
    gridStyle,
    handleSubmit,
  }) => {
    const [visibleCategory, setVisibleCategory] = useState(categories[0]);

    return (
      <>
        <CategorySelector>
          {categories.map((c) => (
            <CategoryButton
              key={c.name}
              selected={c.name === visibleCategory.name}
              onClick={() => setVisibleCategory(c)}
            >
              {c.name}
            </CategoryButton>
          ))}
        </CategorySelector>
        <ItemGrid
          pages={pages}
          itemsMobile={itemsMobile}
          itemsDesktop={itemsDesktop}
          gridStyle={gridStyle}
          items={visibleCategory.items}
        />
      </>
    );
  };

  return (
    <>
      <ConfigScreenWrapper>
        {shouldShowSlots && (
          <SlotSelector
            pages={pages}
            instruction={instruction}
            handleSubmit={handleSubmit}
          />
        )}
        {shouldBreakConfigLoop(shoot.shootProducts, props.location) &&
        shouldShowItems ? (
          <></>
        ) : shouldShowItems ? (
          <ItemPanel>
            {screenSize !== 'Desktop' && (
              <h3>
                {name} <br />
                <Instruction>{instruction}</Instruction>
              </h3>
            )}
            {children ? (
              children
            ) : categories ? (
              <CategoryGrid
                itemsDesktop={itemsDesktop}
                itemsMobile={itemsMobile}
                gridStyle={gridStyle}
                categories={categories}
                pages={pages}
                handleSubmit={handleSubmit}
              />
            ) : (
              <ItemGrid
                itemsDesktop={itemsDesktop}
                itemsMobile={itemsMobile}
                gridStyle={gridStyle}
                items={items}
                pages={pages}
              />
            )}
          </ItemPanel>
        ) : (
          <div />
        )}
        {shouldBreakConfigLoop(shoot.shootProducts, props.location) &&
          (requiredProductFields.length > 0 ? (
            <DoneWrapper>
              <RequiredProductForm
                requiredFields={requiredProductFields}
                register={register}
                control={control}
              />
              <ProductOptionsSelector
                product={product}
                selectedOptions={selectedProductOptions}
                onOptionChange={handleProductOptionChange}
              />
              <DoneScreen product={product} onDone={submitOrder} />
            </DoneWrapper>
          ) : (
            <DoneWrapper>
              <ProductOptionsSelector
                product={product}
                selectedOptions={selectedProductOptions}
                onOptionChange={handleProductOptionChange}
              />
              <DoneScreen product={product} onDone={submitOrder} />
            </DoneWrapper>
          ))}
      </ConfigScreenWrapper>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    additionalData: state.additionalData,
    shoot: state.shoot,
    order: state.v3Order,
    hasPoses: state.additionalData?.availablePoses?.length,
  };
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      setProductConfig,
      clearProductConfig,
      addToConfigStack,
    },
    dispatch,
  );
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ConfigScreen),
);
